<template>
  <div class="skin-grid" :style="{ gridTemplateColumns: `repeat(auto-fill, minmax(${cardSize}px, 1fr))` }">
    <div
      v-for="skin in skins"
      :key="skin.id"
      class="skin-card"
      :class="{ 'skin-card--selected': selectionMode && selectedIds.includes(String(skin.id)) }"
      @click="handleClick(skin)"
    >
      <div class="skin-image-wrapper">
        <img :src="skin.thumbnailUrl" :alt="skin.name" class="skin-image" loading="lazy" />

        <!-- Hover overlay (when showInfo is false) -->
        <div v-if="!showInfo" class="skin-hover-info">
          <div class="skin-name">{{ skin.name }}</div>
          <div class="skin-detail">
            <span>{{ skin.owner }}</span>
            <span>{{ formatDate(skin.createdAt) }}</span>
          </div>
          <div
            v-if="showVisibilityToggle"
            class="visibility-switch visibility-switch--overlay"
            @click.stop="$emit('toggle-public', skin)"
            :title="skin.isPublic ? 'Public — click to make private' : 'Private — click to make public'"
          >
            <svg v-if="!skin.isPublic" class="vis-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/></svg>
            <svg v-else class="vis-icon" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/></svg>
            <div class="vis-track" :class="{ 'vis-track--on': skin.isPublic }">
              <div class="vis-thumb"></div>
            </div>
            <span class="vis-label" :class="{ 'vis-label--on': skin.isPublic }">{{ skin.isPublic ? 'Public' : 'Private' }}</span>
          </div>
        </div>

        <!-- Selection checkmark overlay -->
        <div v-if="selectionMode && selectedIds.includes(String(skin.id))" class="skin-selected-check">✓</div>
      </div>

      <!-- Below-image info panel (when showInfo is true) -->
      <div v-if="showInfo" class="skin-info">
        <div class="skin-name">{{ skin.name }}</div>
        <div class="skin-detail">
          <span>{{ skin.owner }}</span>
          <span>{{ formatDate(skin.createdAt) }}</span>
        </div>
        <div
          v-if="showVisibilityToggle"
          class="visibility-switch"
          @click.stop="$emit('toggle-public', skin)"
          :title="skin.isPublic ? 'Public — click to make private' : 'Private — click to make public'"
        >
          <svg v-if="!skin.isPublic" class="vis-icon" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z" clip-rule="evenodd"/></svg>
          <svg v-else class="vis-icon" viewBox="0 0 20 20" fill="currentColor"><path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/><path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd"/></svg>
          <div class="vis-track" :class="{ 'vis-track--on': skin.isPublic }">
            <div class="vis-thumb"></div>
          </div>
          <span class="vis-label" :class="{ 'vis-label--on': skin.isPublic }">{{ skin.isPublic ? 'Public' : 'Private' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SkinGrid',

  props: {
    skins: {
      type: Array,
      required: true,
    },
    showInfo: {
      type: Boolean,
      default: true,
    },
    selectionMode: {
      type: Boolean,
      default: false,
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    showVisibilityToggle: {
      type: Boolean,
      default: false,
    },
    cardSize: {
      type: Number,
      default: 150,
    },
  },

  methods: {
    handleClick(skin) {
      if (this.selectionMode) {
        this.$emit('toggle', skin);
      } else {
        this.$emit('card-click', skin);
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '';
      return new Date(dateStr).toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.skin-grid {
  display: grid;
  /* grid-template-columns set via inline style binding */
  gap: 16px;
  margin-bottom: 24px;
  max-height: 560px;
  overflow-y: auto;
  padding-right: 8px;
}

/* Custom scrollbar styling */
.skin-grid::-webkit-scrollbar {
  width: 8px;
}

.skin-grid::-webkit-scrollbar-track {
  background: transparent;
}

.skin-grid::-webkit-scrollbar-thumb {
  background: #444;
  border-radius: 4px;
}

.skin-grid::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.skin-card {
  cursor: pointer;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #ddd;
  transition: box-shadow 0.2s, transform 0.15s;
}

.skin-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.skin-card--selected {
  border: 2px solid #3fb950;
  box-shadow: 0 0 0 2px rgba(63, 185, 80, 0.3);
}

/* Square image */
.skin-image-wrapper {
  position: relative;
  width: 100%;
  padding-top: 100%;
  overflow: hidden;
  background: #e8e8e8;
}

.skin-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.skin-hover-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  padding: 8px;
  opacity: 0;
  transition: opacity 0.2s;
}

.skin-card:hover .skin-hover-info {
  opacity: 1;
}

.skin-hover-info .skin-name {
  color: #fff;
  margin-bottom: 3px;
}

.skin-hover-info .skin-detail {
  color: rgba(255, 255, 255, 0.75);
  margin-bottom: 3px;
}

/* Selection checkmark */
.skin-selected-check {
  position: absolute;
  top: 6px;
  right: 8px;
  color: #3fb950;
  font-size: 1.2rem;
  font-weight: bold;
  text-shadow: 0 0 4px #000, 0 0 8px #000;
  line-height: 1;
}

.skin-info {
  padding: 8px;
}

.skin-name {
  font-size: 13px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
}

.skin-detail {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #888;
  margin-bottom: 4px;
}

.skin-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Visibility toggle */
.visibility-switch {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  user-select: none;
  padding: 3px 2px;
  border-radius: 4px;
  transition: background 0.15s;
}

.visibility-switch:hover {
  background: rgba(41, 128, 185, 0.1);
}

.vis-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: #888;
  transition: color 0.2s;
}

.visibility-switch:hover .vis-icon {
  color: #2980b9;
}

.vis-track {
  position: relative;
  width: 30px;
  height: 16px;
  border-radius: 8px;
  background: #555;
  flex-shrink: 0;
  transition: background 0.2s;
}

.vis-track--on {
  background: #2980b9;
}

.vis-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.vis-track--on .vis-thumb {
  left: 16px;
}

.vis-label {
  font-size: 11px;
  color: #aaa;
  transition: color 0.2s;
}

.vis-label--on {
  color: #7eb8e0;
}

.visibility-switch--overlay {
  margin-top: 5px;
}

.visibility-switch--overlay .vis-icon {
  color: rgba(255, 255, 255, 0.7);
}

.visibility-switch--overlay .vis-label {
  color: rgba(255, 255, 255, 0.7);
}

.visibility-switch--overlay .vis-label--on {
  color: #7eb8e0;
}

.visibility-switch--overlay:hover {
  background: rgba(255, 255, 255, 0.1);
}

.visibility-switch--overlay:hover .vis-icon {
  color: #fff;
}
</style>
